import { useNow } from '@lib/hooks/useNow';
import {
  deviceTimeZone,
  listTimeZones,
  timeZoneCity,
  timeZoneDetail,
  timeZoneLabel,
  timeZoneRegion,
  timeZoneSearchText,
} from '@lib/social/scheduling/timeZone';
import type { ScheduleTimeZoneSource } from '@lib/social/scheduling/types';
import dialogCss from '@lib/style/dialog.module.scss';
import formCss from '@lib/style/form.module.scss';
import {
  Autocomplete,
  createFilterOptions,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from '@mui/material';
import { ActionButton } from '@pages/ActionButton';
import { useModalRegistration } from '@stores/ModalStore';
import { scheduleSettings } from '@stores/ScheduleSettings';
import { trackingStore } from '@stores/Tracking';
import { observer } from 'mobx-react-lite';
import css from './TimeZoneDialog.module.scss';

const SUGGESTED_GROUP = 'Suggested';

/**
 * One row of the list. `pin` is what picking it stores: a zone name, or
 * null for the "Your device" row, which is how the schedule goes back to
 * following the clock. `id` keeps the device's zone apart from its own row
 * further down the list, where picking it pins it.
 */
type ZoneOption = {
  id: string;
  zone: string;
  group: string;
  title: string;
  pin: string | null;
};

/**
 * Suggested rows first, then the browser's regional list, which is already in
 * region order (a grouped list must be). Together they seat every zone
 * `isPickableTimeZone` admits, so the pinned zone always has a row: UTC is
 * the suggested row (V8 does not list it) and the regional list has the rest.
 */
const zoneOptions = (): ZoneOption[] => {
  const device = deviceTimeZone();
  const suggested: ZoneOption[] = [
    {
      id: 'device',
      zone: device,
      group: SUGGESTED_GROUP,
      title: `Your device · ${timeZoneCity(device)}`,
      pin: null,
    },
    { id: 'utc', zone: 'UTC', group: SUGGESTED_GROUP, title: 'UTC', pin: 'UTC' },
  ];
  const all = listTimeZones().map(
    (zone): ZoneOption => ({
      id: zone,
      zone,
      group: timeZoneRegion(zone),
      title: timeZoneCity(zone),
      pin: zone,
    }),
  );
  return suggested.concat(all);
};

/**
 * The searchable list, its own component so the zone list and its labels
 * are built only while the dialog is open: the dialog itself renders,
 * closed, on every tick of the compose page's clock.
 */
const TimeZoneList = observer(function TimeZoneList({
  onPick,
}: {
  onPick: (pin: string | null) => void;
}) {
  // Ticking: every row shows the clock there, which the list is open long enough to watch move.
  const at = useNow();
  const options = zoneOptions();
  // The row whose pick is the current state: "Your device" while following
  // the clock, else the pinned zone's row. One exists for every value the
  // store admits (`isPickableTimeZone`), so a miss is a broken invariant.
  const { pinnedTimeZone } = scheduleSettings;
  const current = options.find((o) => o.pin === pinnedTimeZone);
  if (!current) throw new Error(`pinned time zone ${pinnedTimeZone} has no row in the dialog`);
  const filterOptions = createFilterOptions<ZoneOption>({
    stringify: (o) => `${o.title} ${timeZoneSearchText(o.zone, at)}`,
  });
  return (
    <Autocomplete
      options={options}
      value={current}
      disableClearable
      autoHighlight
      openOnFocus
      groupBy={(o) => o.group}
      getOptionLabel={(o) => timeZoneLabel(o.zone, at)}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      filterOptions={filterOptions}
      onChange={(_, o) => onPick(o.pin)}
      renderOption={({ key, ...props }, o) => (
        <li key={key} {...props} className={`${props.className} ${css.option}`}>
          <span className={css.optionTitle}>{o.title}</span>
          <span className={css.optionDetail}>{timeZoneDetail(o.zone, at)}</span>
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          className={formCss.textInput}
          size="small"
          placeholder="Search by city, region or zone name"
          autoFocus
        />
      )}
    />
  );
});

/**
 * "Time zone": the zone the schedule speaks in, as a searchable list of the
 * browser's zones grouped by region, each with its name in words, its
 * offset and the time there now. A pick lands at once in `scheduleSettings`
 * and closes; the "Your device" row at the top is the way back to following
 * the clock. Opened from the compose hint and the schedule header.
 */
export const TimeZoneDialog = observer(function TimeZoneDialog({
  open,
  onClose,
  source,
}: {
  open: boolean;
  onClose: () => void;
  source: ScheduleTimeZoneSource;
}) {
  useModalRegistration('schedule-time-zone', open);

  const pick = (pin: string | null) => {
    scheduleSettings.pinTimeZone(pin);
    trackingStore.scheduleTimeZoneChanged({
      timeZone: scheduleSettings.timeZone,
      followsDevice: pin === null,
      source,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} className={dialogCss.dialog}>
      <DialogTitle className={dialogCss.dialogTitle}>Time zone</DialogTitle>
      <DialogContent className={dialogCss.dialogContent}>
        <p className={css.intro}>
          The schedule and the publish time picker show times in this zone. Remembered on this
          device.
        </p>
        <TimeZoneList onPick={pick} />
      </DialogContent>
      <DialogActions className={dialogCss.dialogActions}>
        <ActionButton variant="secondary" onClick={onClose}>
          Cancel
        </ActionButton>
      </DialogActions>
    </Dialog>
  );
});
